"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Send, Paperclip } from "lucide-react";
import { useChatStore } from "@/lib/chat-store";
import { cn } from "@/lib/utils";

const QUICK_REPLIES = [
  "Baik, terima kasih! 😊",
  "Boleh info harganya?",
  "Saya tertarik, lanjut ya",
];

export function ChatInput() {
  const { activeConversationId, sendMessage } = useChatStore();
  const [text, setText] = useState("");
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasText = text.trim().length > 0;

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = 20;
    const maxHeight = lineHeight * 4;
    const scrollHeight = el.scrollHeight;
    el.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [text, adjustHeight]);

  const handleSend = useCallback(() => {
    if (!hasText || !activeConversationId) return;
    sendMessage(activeConversationId, text.trim());
    setText("");
    setShowQuickReplies(false);
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [hasText, activeConversationId, text, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickReply = (reply: string) => {
    if (!activeConversationId) return;
    sendMessage(activeConversationId, reply);
    setShowQuickReplies(false);
  };

  return (
    <div className="flex flex-col gap-2 pt-2">
      {/* Quick reply suggestions */}
      {showQuickReplies && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          className="flex items-center gap-2 px-1 overflow-x-auto scrollbar-none"
        >
          {QUICK_REPLIES.map((reply, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => handleQuickReply(reply)}
              className={cn(
                "shrink-0 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap",
                "border border-gold/30",
                "text-gold-dark dark:text-gold-light",
                "hover:bg-gold/10 active:bg-gold/20",
                "transition-colors duration-200",
              )}
            >
              {reply}
            </motion.button>
          ))}
        </motion.div>
      )}

      {/* Input bar */}
      <div
        className={cn(
          "flex items-end gap-2 px-3 py-2 rounded-[24px]",
          "bg-dark-card dark:bg-dark-card bg-light-card",
          "border border-white/[0.08] dark:border-white/[0.08] border-black/[0.06]",
          "shadow-sm",
        )}
      >
        {/* Attachment button */}
        <button
          type="button"
          aria-label="Lampirkan file"
          className={cn(
            "shrink-0 flex items-center justify-center w-8 h-8 rounded-full",
            "text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary",
            "hover:text-gold hover:bg-gold/10",
            "transition-colors duration-200",
          )}
        >
          <Paperclip className="w-4 h-4" strokeWidth={1.8} />
        </button>

        {/* Text input */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ketik pesan..."
          rows={1}
          className={cn(
            "flex-1 resize-none bg-transparent py-1.5 px-0",
            "text-sm leading-5",
            "text-dark-text-primary dark:text-dark-text-primary text-light-text-primary",
            "placeholder:text-dark-text-secondary/50 dark:placeholder:text-dark-text-secondary/50 placeholder:text-light-text-secondary/50",
            "outline-none border-none",
            "max-h-[80px] overflow-y-auto scrollbar-none",
          )}
        />

        {/* Send button */}
        <motion.button
          type="button"
          onClick={handleSend}
          disabled={!hasText}
          aria-label="Kirim pesan"
          animate={{
            backgroundColor: hasText ? "#75350E" : "transparent",
            scale: hasText ? 1 : 0.95,
          }}
          transition={{ duration: 0.2 }}
          className={cn(
            "shrink-0 flex items-center justify-center w-8 h-8 rounded-full",
            "transition-shadow duration-200",
            hasText
              ? "shadow-md shadow-gold/30 hover:shadow-lg hover:shadow-gold/40"
              : "cursor-default",
          )}
        >
          <Send
            className={cn(
              "w-4 h-4 transition-colors duration-200",
              hasText
                ? "text-white"
                : "text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary",
            )}
            strokeWidth={2}
          />
        </motion.button>
      </div>
    </div>
  );
}
